/**
 * Partner Store
 *
 * Manages partner-finding state: searching for candidates,
 * sending/receiving partner requests for group/pair challenges.
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import type { PartnerCandidate, PartnerRequest, PartnerSearchFilters } from '../types'
import { partnerApi } from '../api'

interface PartnerState {
  // Search
  candidates: PartnerCandidate[]
  isSearching: boolean

  // Requests
  myRequests: PartnerRequest[]
  isLoadingRequests: boolean
  isSaving: boolean
}

interface PartnerActions {
  searchCandidates: (filters: PartnerSearchFilters) => Promise<void>
  sendRequest: (data: { challengeId: string; recipientUserId: string; message?: string }) => Promise<boolean>
  fetchMyRequests: () => Promise<void>
  respondToRequest: (requestId: string, decision: 'accepted' | 'declined') => Promise<boolean>
  cancelRequest: (requestId: string) => Promise<boolean>
}

type PartnerStore = PartnerState & PartnerActions

export const usePartnerStore = createWithEqualityFn<PartnerStore>()((set) => ({
  // State
  candidates: [],
  isSearching: false,
  myRequests: [],
  isLoadingRequests: false,
  isSaving: false,

  // Actions
  searchCandidates: async (filters) => {
    set({ isSearching: true })
    const response = await partnerApi.searchCandidates(filters)
    if (response.success) {
      set({ candidates: response.data, isSearching: false })
    } else {
      set({ isSearching: false })
    }
  },

  sendRequest: async (data) => {
    set({ isSaving: true })
    const response = await partnerApi.sendRequest(data)
    set({ isSaving: false })
    if (response.success) {
      set((state) => ({
        myRequests: [response.data, ...state.myRequests],
      }))
      return true
    }
    return false
  },

  fetchMyRequests: async () => {
    set({ isLoadingRequests: true })
    const response = await partnerApi.getMyRequests()
    if (response.success) {
      set({ myRequests: response.data, isLoadingRequests: false })
    } else {
      set({ isLoadingRequests: false })
    }
  },

  respondToRequest: async (requestId, decision) => {
    set({ isSaving: true })
    const response = await partnerApi.respondToRequest(requestId, decision)
    set({ isSaving: false })
    if (response.success) {
      set((state) => ({
        myRequests: state.myRequests.map((r) =>
          r.id === requestId ? response.data : r,
        ),
      }))
      return true
    }
    return false
  },

  cancelRequest: async (requestId) => {
    set({ isSaving: true })
    const response = await partnerApi.cancelRequest(requestId)
    set({ isSaving: false })
    if (response.success) {
      set((state) => ({
        myRequests: state.myRequests.map((r) =>
          r.id === requestId ? response.data : r,
        ),
      }))
      return true
    }
    return false
  },
}))

// Selectors
export const selectPendingReceivedRequests = (state: PartnerStore) =>
  state.myRequests.filter((r) => r.status === 'pending' && r.recipient)

export const selectPendingSentRequests = (state: PartnerStore) =>
  state.myRequests.filter((r) => r.status === 'pending' && r.sender)
